export const MarketplaceNavbar = {
  flow: 'x',
  align: 'center space-between',
  padding: 'Y A',
  borderBottom: '1px solid line',
  gap: 'A',

  MarketplaceTitle: {
    tag: 'h2',
    text: 'Marketplace',
    fontSize: 'B',
    fontWeight: '700',
    color: 'title'
  },

  // Search input wrapped so the Clear (×) button can overlay the
  // right edge. `position: relative` on the wrap anchors the absolute
  // button; `flex: 1` + `maxWidth: 'H'` move from the input up to the
  // wrap so layout stays identical.
  SearchWrap: {
    flex: '1',
    maxWidth: 'H',
    position: 'relative',

    SearchInput: {
      extends: 'Input',
      width: '100%',
      placeholder: 'Search libraries, templates, integrations…',
      value: (el, s) => s.query,
      onInput: (e, el, s) => s.update({ query: e.target.value }),
      // Reserve room on the right for the × button so text doesn't
      // collide with it when filled.
      paddingRight: 'B',
      // Marker for the global `/` and Cmd/Ctrl+K shortcuts (see
      // `registerEscHandler` in functions/index.js). `attr: {}` is the
      // right wrapper here — `data-*` is not surfaced as a first-class
      // DOMQL prop.
      attr: { 'data-marketplace-search': '1' }
    },

    // × appears only when there's a query to clear. Sits inside the
    // input's reserved right padding. Clicking clears + refocuses the
    // input so the user can immediately type a new query.
    SearchClearBtn: {
      extends: 'Button',
      hide: (el, s) => !s.query,
      text: '✕',
      title: 'Clear search',
      ariaLabel: 'Clear search',
      position: 'absolute',
      right: 'X',
      top: '50%',
      transform: 'translateY(-50%)',
      padding: 'X Y',
      background: 'transparent',
      color: 'caption',
      fontSize: 'Y',
      onClick: (e, el, s) => {
        s.update({ query: '' })
        // Refocus via registered function — keeps the component free
        // of raw DOM queries (Rule 30).
        el.call('focusSearchInput')
      }
    }
  },

  PricingTabs: {
    flow: 'x',
    gap: 'Y',
    childExtends: 'MarketplacePricingTab',
    // Labels + "Installed (N)" / "Pinned (N)" counters live in
    // MarketplacePricingTab itself so they can be reactive. Installed
    // + Pinned are virtual pricing-tabs — filter is `state.activePricing
    // === '<tab>'` + `matchesFilters` (functions/index.js) checks the
    // corresponding `host.installed` / `host.pinnedIds` list.
    children: { all: {}, free: {}, paid: {}, installed: {}, pinned: {} }
  },

  // Sort controls — `state.activeSort` ∈ 'name' | 'priceAsc' | 'priceDesc'.
  // MarketplaceGrid.children applies the sort after filter; see
  // functions/index.js#sortItems.
  SortTabs: {
    flow: 'x',
    gap: 'Y',
    childExtends: 'MarketplaceSortTab',
    children: { name: {}, priceAsc: {}, priceDesc: {} }
  },

  // Random item picker. Opens detail for a random visible item —
  // respects active filters so the pick stays relevant to the current
  // browse. Hidden when nothing matches current filters.
  SurpriseBtn: {
    extends: 'Button',
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      return el.call('resultsCountLabel', host).startsWith('0')
    },
    text: '🎲 Surprise me',
    title: 'Open a random visible item',
    ariaLabel: 'Open random item',
    fontSize: 'Y',
    padding: 'X A',
    background: 'transparent',
    color: 'caption',
    onClick: (e, el) => {
      const host = el.call('marketplaceHostOf', el)
      el.call('surpriseMe', host)
    }
  },

  // Density toggle: Comfortable ↔ Compact. Label is bidirectional —
  // shows the OTHER option so users know what clicking will do
  // ("Compact" button while in Comfortable mode).
  DensityToggleBtn: {
    extends: 'Button',
    text: (el) => {
      const host = el.call('marketplaceHostOf', el)
      return host?.gridDensity === 'compact' ? 'Comfortable' : 'Compact'
    },
    title: 'Toggle grid density',
    fontSize: 'Y',
    padding: 'X A',
    background: 'transparent',
    color: 'caption',
    onClick: (e, el) => {
      const host = el.call('marketplaceHostOf', el)
      el.call('toggleGridDensity', host)
    }
  },

  // One-click reset for kind + categories + pricing + query. Sort
  // is preserved (user's explicit sort intent). Hidden when nothing
  // is filtered — no clutter on the default view.
  ClearAllBtn: {
    extends: 'Button',
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      return !el.call('hasActiveFilters', host)
    },
    text: 'Clear all',
    title: 'Reset every filter to its default',
    fontSize: 'Y',
    padding: 'X A',
    background: 'transparent',
    color: 'caption',
    onClick: (e, el) => {
      const host = el.call('marketplaceHostOf', el)
      el.call('clearAllFilters', host)
    }
  },

  // Compose current filter state into a shareable URL (canvas page's
  // `_applyFiltersFromUrl` consumes the same keys). See
  // `composeShareUrl` + `copyLinkToClipboard` in functions/index.js.
  MarketplaceCopyLinkBtn: {}
}
